import { AgGridLinesTooltip, useAssetsContext, type ColumnDef } from '@talos/kyoko';
import type { ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../../hooks';
import type { CrossAssetRiskGroup } from '../SubAccountTradingLimits/crossAssetRiskGroupsApi';
import { ALL_ASSETS_LABEL, NO_ASSETS_LABEL, isCrossAssetRiskGroupAllAssets } from './utils';

/**
 * Returns the stable blotter column definition for the cross-asset risk group EntityAdmin page.
 */
export function useCrossAssetRiskGroupColumns(): ColumnDef<CrossAssetRiskGroup>[] {
  const { getAssetDisplaySymbol } = useAssetsContext();
  const { enableCrossAssetRiskGroupDeltaRiskCheck } = useFeatureFlag();

  return useMemo(
    () => [
      {
        field: 'Name',
        type: 'text',
        title: 'Group Name',
      },
      {
        field: 'DisplayName',
        type: 'text',
        title: 'Display Name',
      },
      {
        field: 'ExposureCurrency',
        type: 'currency',
        title: 'Exposure Currency',
      },
      {
        id: 'Assets',
        type: 'custom',
        title: 'Assets',
        initialWidth: 300,
        params: {
          headerName: 'Assets',
          valueGetter: ({ data }) => getAssetSymbols(data?.Assets),
          valueFormatter: ({ value }: ValueFormatterParams<CrossAssetRiskGroup, string[] | undefined>) =>
            getCrossAssetRiskGroupAssetsDisplayValue(
              value,
              getAssetDisplaySymbol,
              enableCrossAssetRiskGroupDeltaRiskCheck
            ).join(', '),
          tooltipValueGetter: ({ value }) => {
            const assets = getCrossAssetRiskGroupAssetsDisplayValue(
              value,
              getAssetDisplaySymbol,
              enableCrossAssetRiskGroupDeltaRiskCheck
            );
            return assets.length > 0 ? assets : null;
          },
          tooltipComponent: AgGridLinesTooltip,
        },
      },
      {
        field: 'Description',
        type: 'text',
      },
    ],
    [enableCrossAssetRiskGroupDeltaRiskCheck, getAssetDisplaySymbol]
  );
}

export function getCrossAssetRiskGroupAssetsDisplayValue(
  assets: string[] | null | undefined,
  getAssetDisplaySymbol: ReturnType<typeof useAssetsContext>['getAssetDisplaySymbol'],
  emptyGroupMeansAllAssets = false
) {
  const assetSymbols = getAssetSymbols(assets);
  if (assetSymbols.length === 0) {
    return [emptyGroupMeansAllAssets ? ALL_ASSETS_LABEL : NO_ASSETS_LABEL];
  }

  return assetSymbols.map(asset =>
    isCrossAssetRiskGroupAllAssets(asset) ? ALL_ASSETS_LABEL : getAssetDisplaySymbol(asset) || asset
  );
}

function getAssetSymbols(assets: CrossAssetRiskGroup['Assets'] | null | undefined) {
  return assets?.filter((asset): asset is string => Boolean(asset)) ?? [];
}
